import type { FidexaModulesType } from "./moduleTemplateUtils";
import { parseJwtUtils } from "./parseJwt";

// Resumo não secreto da sessão (AUTH-1). Tela e rota leem daqui, e não do
// token, para que o access token possa sair do JavaScript sem perder as
// leituras síncronas. Sem o bloco `session` na resposta da API, toda leitura
// cai no JWT em storage, exatamente como antes deste módulo existir.

export type SessionTransport = "bearer" | "cookie";

export type FidexaSession = {
  user_id: string;
  user_name: string;
  company_group_id: string;
  session_id: string;
  permissions_version: string;
  /** Epoch em segundos, o mesmo `exp` do access token. */
  expires_at: number;
  transport: SessionTransport;
};

export const FIDEXA_SESSION_KEY = "fidexa_session";

const readAccessToken = () => localStorage.getItem("access_token");

const isFilledString = (value: unknown): value is string =>
  typeof value === "string" && value !== "";

const isFidexaSession = (value: unknown): value is FidexaSession => {
  if (typeof value !== "object" || value === null) return false;

  const session = value as Record<string, unknown>;

  return (
    isFilledString(session.user_id) &&
    isFilledString(session.user_name) &&
    isFilledString(session.company_group_id) &&
    isFilledString(session.session_id) &&
    isFilledString(session.permissions_version) &&
    typeof session.expires_at === "number" &&
    Number.isFinite(session.expires_at) &&
    (session.transport === "bearer" || session.transport === "cookie")
  );
};

const parseStoredSession = (raw: string | null): FidexaSession | null => {
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    return isFidexaSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const tokenBelongsTo = (token: string, userId: string): boolean => {
  try {
    return parseJwtUtils.getUserId(token) === userId;
  } catch {
    return false;
  }
};

/**
 * Resumo em que dá para confiar. No modo bearer ele só vale ao lado de um
 * token do mesmo usuário: um bundle anterior ao resumo troca de usuário sem
 * apagá-lo, e o resumo velho não pode mostrar outro nome ou grupo nem manter
 * uma rota protegida aberta.
 */
const getActiveSession = (): FidexaSession | null => {
  const session = parseStoredSession(localStorage.getItem(FIDEXA_SESSION_KEY));

  if (!session || session.transport === "cookie") return session;

  const token = readAccessToken();

  return token && tokenBelongsTo(token, session.user_id) ? session : null;
};

/** Sem resumo válido na resposta, um resumo anterior não pode sobreviver. */
const store = (session: unknown) => {
  if (isFidexaSession(session)) {
    localStorage.setItem(FIDEXA_SESSION_KEY, JSON.stringify(session));
    return;
  }

  localStorage.removeItem(FIDEXA_SESSION_KEY);
};

const clear = () => {
  localStorage.removeItem(FIDEXA_SESSION_KEY);
};

const isCookieMode = (): boolean =>
  getActiveSession()?.transport === "cookie";

/** Vale para cookie: o relógio do token, sem o token. */
const isSessionExpired = (): boolean => {
  const session = getActiveSession();
  return !session || Date.now() >= session.expires_at * 1000 - 5000;
};

/** Identifica a credencial em uso, para saber se outra aba já renovou. */
const credentialMarker = (): string | undefined => {
  const session = getActiveSession();

  return session?.transport === "cookie"
    ? String(session.expires_at)
    : (readAccessToken() ?? undefined);
};

const hasSession = (): boolean =>
  Boolean(readAccessToken()) || isCookieMode();

const getUserId = (): string | null =>
  getActiveSession()?.user_id ??
  parseJwtUtils.getUserId(readAccessToken() ?? "");

const getUserName = (): string | null =>
  getActiveSession()?.user_name ??
  parseJwtUtils.getUserName(readAccessToken() ?? "");

const getCompanyGroupId = (): string | null =>
  getActiveSession()?.company_group_id ??
  parseJwtUtils.getGroupSid(readAccessToken() ?? "");

// O produto opera só com CHARGE: o módulo depende de haver sessão, não do
// conteúdo do token (ver `parseJwtUtils.getModules`).
const getModules = (): FidexaModulesType[] =>
  hasSession() ? ["CHARGE"] : [];

export const authSession = {
  getActiveSession,
  store,
  clear,
  hasSession,
  isCookieMode,
  isSessionExpired,
  credentialMarker,
  getUserId,
  getUserName,
  getCompanyGroupId,
  getModules,
};
